/**
 * 使用说明页面配置
 * 通过配置驱动左侧导航与右侧详情展示
 */

export interface UsageStep {
  title: string;
  details: string[];
}

export interface UsageSection {
  id: string;
  title: string;
  category: string;
  summary: string;
  goal: string;
  steps: UsageStep[];
  tips?: string[];
  screenshotPlaceholder?: string;
}

export const USAGE_CATEGORIES = {
  SYSTEM_OVERVIEW: "系统概览",
  DEVICE_MANAGEMENT: "设备管理",
  CATEGORY_MANAGEMENT: "分类管理",
  MANUFACTURER_MANAGEMENT: "制造商管理",
  MODEL_MANAGEMENT: "型号管理",
  VISUALIZATION: "可视化展示",
  API_DOCUMENTATION: "API文档",
  SERVICE_TOKEN: "服务Token机制",
} as const;

export const USAGE_SECTIONS: UsageSection[] = [
  {
    id: "system-overview",
    title: "msDevice 系统整体介绍",
    category: USAGE_CATEGORIES.SYSTEM_OVERVIEW,
    summary:
      "介绍 msDevice 设备管理系统的定位、核心功能模块，以及各功能页面之间的协作关系。",
    goal:
      "帮助用户快速理解设备管理系统的整体架构，了解如何通过不同功能模块完成设备全生命周期管理。",
    screenshotPlaceholder: "待补截图：system-overview.png",
    steps: [
      {
        title: "系统定位与价值",
        details: [
          "msDevice 是平台中的设备管理微服务，负责统一管理所有设备信息。",
          "系统提供设备分类、制造商、型号、设备实例的完整管理能力。",
          "支持设备与空间（Space）的关联，实现设备的位置管理和可视化展示。",
        ],
      },
      {
        title: "核心功能模块",
        details: [
          "**设备列表管理**：管理设备实例，包括新增、编辑、删除、状态切换等操作。",
          "**设备分类管理**：维护设备分类树，支持一级和二级分类，便于设备归类。",
          "**制造商管理**：维护设备制造商信息，为设备型号提供制造商关联。",
          "**设备型号管理**：管理设备型号，关联分类和制造商，作为设备实例的模板。",
          "**空间可视化**：以平面图、地图、3D等方式展示设备在空间中的分布。",
          "**API文档**：查看后端所有接口的详细说明和使用方法。",
        ],
      },
      {
        title: "数据关联关系",
        details: [
          "设备实例（Device）关联设备型号（DeviceModel），设备型号关联分类（Category）和制造商（Manufacturer）。",
          "设备实例可以关联空间（Space），实现设备的位置管理。",
          "分类采用树形结构，支持一级和二级分类，便于设备分类管理。",
        ],
      },
    ],
    tips: [
      "建议先创建分类和制造商，再创建设备型号，最后创建设备实例。",
      "设备分类的层级结构应该根据实际业务需求设计，避免层级过深。",
      "定期维护制造商和型号信息，确保数据的准确性和完整性。",
    ],
  },
  {
    id: "device-list-management",
    title: "设备列表管理（/list）",
    category: USAGE_CATEGORIES.DEVICE_MANAGEMENT,
    summary: "管理设备实例，包括设备的增删改查、状态切换、详情查看等功能。",
    goal: "帮助用户快速掌握设备管理的基本操作，高效完成设备信息的维护。",
    screenshotPlaceholder: "待补截图：device-list.png",
    steps: [
      {
        title: "查看设备列表",
        details: [
          "页面顶部显示设备统计卡片，包括总设备数、在线设备数、离线设备数等关键指标。",
          "列表支持按设备型号、空间、状态等条件筛选，快速定位目标设备。",
          "表格支持列设置、列宽调整、排序等功能，可根据需要自定义显示。",
        ],
      },
      {
        title: "搜索与筛选",
        details: [
          "使用搜索面板按设备名称、型号、空间等条件进行筛选。",
          "支持多条件组合查询，提高查询效率。",
          "点击「重置」按钮可清空所有筛选条件，恢复默认列表。",
        ],
      },
      {
        title: "新增设备",
        details: [
          "点击「新增」按钮打开设备表单弹窗。",
          "填写设备名称、选择设备型号、关联空间等必填信息。",
          "设备型号和空间支持级联选择，便于快速定位。",
          "保存后设备创建成功，可在列表中查看。",
        ],
      },
      {
        title: "编辑设备",
        details: [
          "点击表格中的「编辑」按钮，打开设备编辑表单。",
          "可以修改设备名称、型号、空间、状态等信息。",
          "修改设备型号时，系统会验证型号是否存在且已启用。",
          "保存后修改立即生效。",
        ],
      },
      {
        title: "查看设备详情",
        details: [
          "点击表格中的「详情」按钮，打开设备详情弹窗。",
          "详情页面展示设备的完整信息，包括基本信息、关联的型号、空间等。",
          "详情信息为只读，如需修改请使用编辑功能。",
        ],
      },
      {
        title: "设备状态管理",
        details: [
          "设备支持启用/禁用状态切换。",
          "禁用的设备不会在部分查询中显示，但数据不会删除。",
          "状态切换操作需要确认，避免误操作。",
        ],
      },
      {
        title: "删除设备",
        details: [
          "点击「删除」按钮，系统会弹出确认对话框。",
          "删除操作不可恢复，请谨慎操作。",
          "如果设备有关联数据，删除可能会失败，需要先解除关联。",
        ],
      },
    ],
    tips: [
      "建议在创建设备前，先确保设备型号和空间信息已创建。",
      "定期检查设备状态，及时处理异常设备。",
      "使用筛选功能可以快速定位特定类型的设备。",
      "设备名称应该具有唯一性和可识别性，便于后续管理。",
    ],
  },
  {
    id: "category-management",
    title: "设备分类管理（/categories）",
    category: USAGE_CATEGORIES.CATEGORY_MANAGEMENT,
    summary: "维护设备分类树，支持一级和二级分类，便于设备归类和管理。",
    goal: "帮助管理员创建和维护设备分类体系，确保设备分类的规范性和一致性。",
    screenshotPlaceholder: "待补截图：category-management.png",
    steps: [
      {
        title: "查看分类树",
        details: [
          "左侧显示设备分类树，支持展开和折叠。",
          "分类树采用树形结构，一级分类下可以有多个二级分类。",
          "点击分类节点，右侧会显示该分类的详细信息。",
        ],
      },
      {
        title: "创建一级分类",
        details: [
          "点击左侧树形面板的「新增」按钮，打开分类表单。",
          "填写分类名称、描述等信息，选择分类图标（可选）。",
          "一级分类不需要选择父分类，直接保存即可。",
          "保存后分类会出现在分类树中。",
        ],
      },
      {
        title: "创建二级分类",
        details: [
          "在一级分类节点上点击「+」按钮，或选中一级分类后点击「新增子分类」。",
          "填写分类名称、描述等信息。",
          "系统会自动关联到选中的一级分类作为父分类。",
          "保存后二级分类会出现在对应一级分类下。",
        ],
      },
      {
        title: "编辑分类",
        details: [
          "选中分类节点，点击右侧详情面板的「编辑」按钮。",
          "可以修改分类名称、描述、图标、启用状态等信息。",
          "修改父分类时，系统会验证父分类是否存在且已启用。",
          "保存后修改立即生效。",
        ],
      },
      {
        title: "查看分类详情",
        details: [
          "点击分类节点，右侧详情面板会显示分类的完整信息。",
          "包括分类名称、描述、层级、父分类、图标、启用状态等。",
          "详情信息为只读，如需修改请使用编辑功能。",
        ],
      },
      {
        title: "删除分类",
        details: [
          "选中分类节点，点击「删除」按钮，系统会弹出确认对话框。",
          "删除分类前，需要确保该分类下没有关联的设备型号。",
          "如果分类下有关联数据，删除会失败，需要先解除关联。",
          "删除操作不可恢复，请谨慎操作。",
        ],
      },
      {
        title: "分类搜索",
        details: [
          "分类树支持搜索功能，可以快速定位目标分类。",
          "输入关键词后，系统会高亮匹配的分类节点。",
          "搜索支持分类名称的模糊匹配。",
        ],
      },
    ],
    tips: [
      "建议先规划好分类体系，再开始创建分类。",
      "分类名称应该简洁明了，便于识别和记忆。",
      "一级分类应该覆盖主要的设备类型，二级分类用于细分。",
      "定期检查分类的使用情况，及时清理未使用的分类。",
      "分类图标建议使用 SVG 格式，确保显示效果。",
    ],
  },
  {
    id: "manufacturer-management",
    title: "制造商管理（/manufacturers）",
    category: USAGE_CATEGORIES.MANUFACTURER_MANAGEMENT,
    summary: "维护设备制造商信息，为设备型号提供制造商关联。",
    goal: "帮助管理员维护制造商数据，确保制造商信息的准确性和完整性。",
    screenshotPlaceholder: "待补截图：manufacturer-management.png",
    steps: [
      {
        title: "查看制造商列表",
        details: [
          "页面顶部显示制造商统计卡片，包括总制造商数、启用数量等指标。",
          "列表展示制造商名称、描述、启用状态等基本信息。",
          "支持按名称、状态等条件筛选。",
        ],
      },
      {
        title: "搜索与筛选",
        details: [
          "使用搜索面板按制造商名称、状态等条件进行筛选。",
          "支持多条件组合查询，提高查询效率。",
          "点击「重置」按钮可清空所有筛选条件。",
        ],
      },
      {
        title: "新增制造商",
        details: [
          "点击「新增」按钮打开制造商表单弹窗。",
          "填写制造商名称、描述等必填信息。",
          "制造商名称应该具有唯一性，系统会进行验证。",
          "保存后制造商创建成功，可在列表中查看。",
        ],
      },
      {
        title: "编辑制造商",
        details: [
          "点击表格中的「编辑」按钮，打开制造商编辑表单。",
          "可以修改制造商名称、描述、启用状态等信息。",
          "修改制造商名称时，系统会验证名称的唯一性。",
          "保存后修改立即生效。",
        ],
      },
      {
        title: "删除制造商",
        details: [
          "点击「删除」按钮，系统会弹出确认对话框。",
          "删除制造商前，需要确保没有设备型号关联该制造商。",
          "如果制造商下有关联的设备型号，删除会失败，需要先解除关联。",
          "删除操作不可恢复，请谨慎操作。",
        ],
      },
      {
        title: "启用/禁用制造商",
        details: [
          "制造商支持启用/禁用状态切换。",
          "禁用的制造商不会在设备型号创建时显示，但已关联的设备型号不受影响。",
          "状态切换操作需要确认，避免误操作。",
        ],
      },
    ],
    tips: [
      "制造商名称应该使用官方名称，保持一致性。",
      "建议在创建设备型号前，先创建好制造商信息。",
      "定期检查制造商的使用情况，及时清理未使用的制造商。",
      "制造商描述可以包含官方网站、联系方式等补充信息。",
    ],
  },
  {
    id: "model-management",
    title: "设备型号管理（/models）",
    category: USAGE_CATEGORIES.MODEL_MANAGEMENT,
    summary: "管理设备型号，关联分类和制造商，作为设备实例的模板。",
    goal: "帮助管理员维护设备型号信息，确保设备型号数据的准确性和完整性。",
    screenshotPlaceholder: "待补截图：model-management.png",
    steps: [
      {
        title: "查看型号列表",
        details: [
          "页面顶部显示型号统计卡片，包括总型号数、启用数量等指标。",
          "列表展示型号名称、分类、制造商、启用状态等基本信息。",
          "支持按分类、制造商、状态等条件筛选。",
        ],
      },
      {
        title: "搜索与筛选",
        details: [
          "使用搜索面板按型号名称、分类、制造商、状态等条件进行筛选。",
          "分类和制造商支持级联选择，便于快速定位。",
          "支持多条件组合查询，提高查询效率。",
        ],
      },
      {
        title: "新增设备型号",
        details: [
          "点击「新增」按钮打开型号表单弹窗。",
          "填写型号名称、选择分类（支持一级和二级分类）、选择制造商等必填信息。",
          "分类和制造商必须选择已启用的选项。",
          "可以填写型号描述、规格参数等补充信息。",
          "保存后型号创建成功，可在列表中查看。",
        ],
      },
      {
        title: "编辑设备型号",
        details: [
          "点击表格中的「编辑」按钮，打开型号编辑表单。",
          "可以修改型号名称、分类、制造商、描述、启用状态等信息。",
          "修改分类或制造商时，系统会验证选项是否存在且已启用。",
          "保存后修改立即生效。",
        ],
      },
      {
        title: "删除设备型号",
        details: [
          "点击「删除」按钮，系统会弹出确认对话框。",
          "删除型号前，需要确保没有设备实例关联该型号。",
          "如果型号下有关联的设备实例，删除会失败，需要先解除关联。",
          "删除操作不可恢复，请谨慎操作。",
        ],
      },
      {
        title: "启用/禁用型号",
        details: [
          "设备型号支持启用/禁用状态切换。",
          "禁用的型号不会在设备创建时显示，但已关联的设备实例不受影响。",
          "状态切换操作需要确认，避免误操作。",
        ],
      },
    ],
    tips: [
      "建议在创建设备型号前，先创建好分类和制造商信息。",
      "型号名称应该包含制造商和型号信息，便于识别。",
      "分类选择应该准确，便于后续按分类筛选设备。",
      "定期检查型号的使用情况，及时清理未使用的型号。",
      "型号描述可以包含技术规格、适用场景等补充信息。",
    ],
  },
  {
    id: "visualization",
    title: "空间可视化（/visual）",
    category: USAGE_CATEGORIES.VISUALIZATION,
    summary: "以平面图、地图、3D等方式展示设备在空间中的分布情况。",
    goal: "帮助用户直观地了解设备在空间中的位置和分布，便于设备管理和维护。",
    screenshotPlaceholder: "待补截图：visualization.png",
    steps: [
      {
        title: "选择可视化方式",
        details: [
          "页面提供多种可视化方式，包括概览、平面图、地图、3D等。",
          "通过标签页切换不同的可视化方式。",
          "每种方式适用于不同的场景和需求。",
        ],
      },
      {
        title: "概览模式",
        details: [
          "概览模式显示设备的整体统计信息和分布情况。",
          "可以快速了解设备的总数、分布情况等关键指标。",
          "适合快速了解设备整体情况。",
        ],
      },
      {
        title: "平面图模式",
        details: [
          "平面图模式以楼层平面图的方式展示设备分布。",
          "可以选择不同的楼层查看该楼层的设备分布。",
          "设备以图标或标记的形式显示在平面图上。",
          "点击设备可以查看设备详情。",
        ],
      },
      {
        title: "地图模式",
        details: [
          "地图模式以地图的方式展示设备的地理位置分布。",
          "支持缩放、拖拽等地图操作。",
          "设备以标记的形式显示在地图上。",
          "适合查看设备的地理分布情况。",
        ],
      },
      {
        title: "3D模式",
        details: [
          "3D模式以三维场景的方式展示设备分布。",
          "支持旋转、缩放等3D操作。",
          "可以更直观地了解设备在空间中的位置关系。",
          "适合需要详细了解设备空间关系的场景。",
        ],
      },
      {
        title: "设备筛选",
        details: [
          "可视化页面支持按设备类型、状态等条件筛选设备。",
          "筛选后只显示符合条件的设备。",
          "可以快速定位特定类型的设备。",
        ],
      },
      {
        title: "设备详情查看",
        details: [
          "在可视化界面中点击设备，可以查看设备详情。",
          "详情信息包括设备名称、型号、状态、位置等。",
          "可以从详情页面跳转到设备列表进行编辑。",
        ],
      },
    ],
    tips: [
      "平面图模式需要先配置楼层平面图，才能正常显示。",
      "地图模式需要设备关联了空间且空间有地理坐标信息。",
      "3D模式需要浏览器支持WebGL，建议使用现代浏览器。",
      "可视化展示的性能与设备数量相关，建议使用筛选功能减少显示数量。",
    ],
  },
  {
    id: "api-documentation",
    title: "API文档（/api-docs）",
    category: USAGE_CATEGORIES.API_DOCUMENTATION,
    summary: "查看后端所有接口的详细说明，包括请求参数、响应格式、权限要求等。",
    goal: "帮助开发者快速了解和使用后端API接口，提高开发效率。",
    screenshotPlaceholder: "待补截图：api-docs.png",
    steps: [
      {
        title: "浏览接口列表",
        details: [
          "左侧显示所有API接口列表，按功能模块分类。",
          "每个分类下包含相关的接口列表。",
          "点击接口可以查看该接口的详细信息。",
        ],
      },
      {
        title: "查看接口详情",
        details: [
          "选中接口后，右侧会显示该接口的详细信息。",
          "包括接口名称、描述、请求方法、路径、权限要求等。",
          "显示请求参数、响应字段、响应示例等详细信息。",
        ],
      },
      {
        title: "理解接口参数",
        details: [
          "接口详情中会详细说明每个请求参数的含义、类型、是否必填等。",
          "路径参数、查询参数、请求体参数会分别说明。",
          "参数示例可以帮助理解参数的使用方法。",
        ],
      },
      {
        title: "查看响应格式",
        details: [
          "接口详情中会说明响应的数据格式。",
          "包括响应字段的含义、类型、示例值等。",
          "响应示例可以帮助理解返回数据的结构。",
        ],
      },
      {
        title: "了解权限要求",
        details: [
          "每个接口都会说明所需的权限要求。",
          "权限要求包括权限代码和权限描述。",
          "调用接口前需要确保用户具有相应的权限。",
        ],
      },
      {
        title: "使用接口",
        details: [
          "根据接口文档中的说明，构造请求参数。",
          "使用正确的请求方法（GET、POST、PUT、DELETE等）。",
          "在请求头中携带必要的认证信息（Token等）。",
          "根据响应结果进行相应的处理。",
        ],
      },
    ],
    tips: [
      "建议在调用接口前，先仔细阅读接口文档。",
      "注意接口的权限要求，确保用户具有相应权限。",
      "请求参数的类型和格式要严格按照文档说明。",
      "响应数据的处理要考虑各种情况，包括成功和失败的情况。",
      "接口文档会随着系统更新而更新，建议定期查看最新版本。",
    ],
  },
  {
    id: "data-relationship",
    title: "数据关联关系说明",
    category: USAGE_CATEGORIES.SYSTEM_OVERVIEW,
    summary: "详细说明系统中各数据实体之间的关联关系和使用规则。",
    goal: "帮助用户理解数据模型，正确建立和维护数据关联关系。",
    screenshotPlaceholder: "待补截图：data-relationship.png",
    steps: [
      {
        title: "核心数据实体",
        details: [
          "**设备分类（Category）**：设备分类树，支持一级和二级分类。",
          "**制造商（Manufacturer）**：设备制造商信息。",
          "**设备型号（DeviceModel）**：设备型号信息，关联分类和制造商。",
          "**设备实例（Device）**：具体的设备实例，关联设备型号和空间。",
          "**空间（Space）**：设备所在的空间位置信息。",
        ],
      },
      {
        title: "关联关系",
        details: [
          "**设备型号 → 分类**：一个设备型号必须关联一个分类（一级或二级）。",
          "**设备型号 → 制造商**：一个设备型号必须关联一个制造商。",
          "**设备实例 → 设备型号**：一个设备实例必须关联一个设备型号。",
          "**设备实例 → 空间**：一个设备实例可以关联一个空间（可选）。",
          "**分类 → 父分类**：二级分类必须关联一个一级分类作为父分类。",
        ],
      },
      {
        title: "创建顺序建议",
        details: [
          "1. **创建分类**：先创建一级分类，再创建二级分类。",
          "2. **创建制造商**：创建设备制造商信息。",
          "3. **创建设备型号**：选择分类和制造商，创建设备型号。",
          "4. **创建设备实例**：选择设备型号和空间，创建设备实例。",
          "",
          "⚠️ **重要**：必须按照顺序创建，否则会出现关联数据不存在的情况。",
        ],
      },
      {
        title: "删除规则",
        details: [
          "**删除分类**：如果分类下有关联的设备型号，不能删除。",
          "**删除制造商**：如果制造商下有关联的设备型号，不能删除。",
          "**删除设备型号**：如果设备型号下有关联的设备实例，不能删除。",
          "**删除设备实例**：设备实例可以删除，但删除后不可恢复。",
          "",
          "⚠️ **注意**：删除操作前，系统会检查关联关系，防止误删除。",
        ],
      },
      {
        title: "启用/禁用规则",
        details: [
          "**禁用分类**：禁用的分类不会在设备型号创建时显示，但已关联的设备型号不受影响。",
          "**禁用制造商**：禁用的制造商不会在设备型号创建时显示，但已关联的设备型号不受影响。",
          "**禁用设备型号**：禁用的设备型号不会在设备创建时显示，但已关联的设备实例不受影响。",
          "**禁用设备实例**：禁用的设备实例不会在部分查询中显示。",
        ],
      },
      {
        title: "最佳实践",
        details: [
          "**分类设计**：",
          "  - 分类体系应该清晰明了，避免层级过深。",
          "  - 一级分类应该覆盖主要的设备类型。",
          "  - 二级分类用于细分，不要创建过多层级。",
          "",
          "**数据维护**：",
          "  - 定期检查数据的完整性和准确性。",
          "  - 及时清理未使用的分类、制造商、型号。",
          "  - 保持数据的一致性，避免重复数据。",
          "",
          "**关联管理**：",
          "  - 创建设备前，确保关联的分类、制造商、型号已创建。",
          "  - 删除数据前，先检查关联关系，避免删除失败。",
          "  - 修改关联关系时，确保新的关联数据存在且已启用。",
        ],
      },
    ],
    tips: [
      "建议在系统初始化时，先建立好分类和制造商的基础数据。",
      "设备型号应该准确关联分类和制造商，便于后续管理和查询。",
      "设备实例关联空间后，可以在可视化页面查看设备位置。",
      "定期检查数据的关联关系，确保数据的完整性和一致性。",
      "删除数据前，务必先检查关联关系，避免误删除。",
    ],
  },
  {
    id: "service-token-overview",
    title: "服务Token机制概述",
    category: USAGE_CATEGORIES.SERVICE_TOKEN,
    summary: "介绍微服务间通信的Token认证机制，包括Token生成、验证和自动更新流程。",
    goal: "帮助开发者理解服务Token的工作原理，正确配置和使用服务Token进行微服务间通信。",
    steps: [
      {
        title: "什么是服务Token",
        details: [
          "服务Token是用于微服务间通信的认证令牌，采用JWT（JSON Web Token）格式。",
          "每个服务都有自己的service_secret（服务密钥），用于向msLogin证明身份。",
          "Token由msLogin统一生成和管理，确保服务间通信的安全性。",
          "Token包含服务信息、权限范围（scopes）、目标服务（audience）等关键信息。",
        ],
      },
      {
        title: "核心概念",
        details: [
          "**service_secret**：每个服务的专属密钥，存储在数据库中，用于向msLogin证明身份。",
          "**Token生成**：服务使用自己的service_secret向msLogin请求Token，msLogin使用目标服务的service_secret签名Token。",
          "**Token验证**：目标服务收到Token后，可以委托msLogin验证（默认），或自己验证（需要配置）。",
          "**自动更新**：服务可以自动从msLogin获取和刷新Token，无需手动管理。",
        ],
      },
      {
        title: "使用场景",
        details: [
          "msDevice需要访问msspace获取空间信息时，使用服务Token进行认证。",
          "任何微服务需要访问其他微服务的受保护接口时，都需要使用服务Token。",
          "服务Token替代了传统的API Key或共享密钥方式，提供更安全的认证机制。",
        ],
      },
    ],
    tips: [
      "每个服务只需要知道自己的service_secret，不需要知道其他服务的密钥。",
      "Token的生成和验证都通过msLogin统一管理，确保密钥安全。",
      "建议使用自动更新机制，减少手动管理Token的工作量。",
    ],
  },
  {
    id: "service-token-generation",
    title: "Token生成逻辑",
    category: USAGE_CATEGORIES.SERVICE_TOKEN,
    summary: "详细说明服务Token的生成流程，包括服务身份验证、Token签名和存储机制。",
    goal: "帮助开发者理解Token生成的完整流程，正确配置服务密钥和Token生成参数。",
    steps: [
      {
        title: "生成流程",
        details: [
          "1. **服务请求Token**：",
          "   - 服务（如msDevice）使用自己的service_secret向msLogin请求Token",
          "   - 请求接口：`POST /api/service-token/get-token`",
          "   - 请求体包含：`service_name`（源服务）、`target_service`（目标服务）、`service_secret`（源服务的密钥）",
          "",
          "2. **msLogin验证服务身份**：",
          "   - 查询数据库：`SELECT service_secret FROM services WHERE service_name = 'msdevice'`",
          "   - 使用安全的字符串比较（`crypto.timingSafeEqual`）验证service_secret",
          "   - 检查服务状态：`is_active = true` 且 `status = 1`",
          "",
          "3. **检查预设Token**：",
          "   - 如果service_token表中已存在有效的预设Token，直接返回",
          "   - 如果Token已过期，自动重新生成",
          "",
          "4. **生成新Token**：",
          "   - 查询目标服务（如msspace）的service_secret",
          "   - 使用目标服务的service_secret签名Token",
          "   - Token包含：`service`、`aud`（目标服务）、`scopes`（权限范围）、`is_service_token`等字段",
          "",
          "5. **存储Token**：",
          "   - 将Token存储到service_token表（如果不存在预设Token）",
          "   - 返回Token给请求的服务",
        ],
      },
      {
        title: "Token生成API",
        details: [
          "**简化接口（推荐）**：`POST /api/service-token/get-token`",
          "```json",
          "{",
          '  "service_name": "msdevice",',
          '  "target_service": "msspace",',
          '  "service_secret": "msdevice的service_secret"',
          "}",
          "```",
          "",
          "**响应示例**：",
          "```json",
          "{",
          '  "success": true,',
          '  "message": "获取服务Token成功",',
          '  "data": {',
          '    "tokenValue": "eyJhbGciOiJIUzI1NiIs...",',
          '    "serviceName": "msdevice",',
          '    "targetService": "msspace",',
          '    "expiresAt": null',
          "  }",
          "}",
          "```",
        ],
      },
      {
        title: "Token预设与自动生成",
        details: [
          "系统支持两种Token生成方式，它们**互补而非冲突**：",
          "",
          "**方式一：预设Token（推荐）**：",
          "  - 在msAdmin中手动创建Token，存储到service_token表",
          "  - 可以设置过期时间、使用次数限制、IP白名单等",
          "  - 管理员可以查看和管理所有Token",
          "",
          "**方式二：自动生成Token**：",
          "  - 通过API自动申请Token",
          "  - msLogin会先检查是否存在预设Token，如果存在且有效则直接返回",
          "  - 如果不存在，自动创建新Token并存储到表",
          "",
          "**两种方式可以共存**：",
          "  - 如果已预设 → 使用预设的Token",
          "  - 如果未预设 → 自动生成新Token",
        ],
      },
      {
        title: "配置要求",
        details: [
          "**msDevice配置**：",
          "  - 环境变量：`MSDEVICE_SERVICE_SECRET`（必须与数据库中msdevice服务的service_secret完全一致）",
          "  - msLogin API地址：`LOGIN_API_URL` 或 `MSLOGIN_URL`",
          "",
          "**msAdmin配置**：",
          "  - 在服务管理页面，为msdevice服务设置service_secret",
          "  - 确保service_secret与msDevice环境变量中的值完全一致",
          "",
          "**重要**：service_secret必须完全一致，否则Token生成会失败！",
        ],
      },
    ],
    tips: [
      "建议在msAdmin中预设Token，可以更好地管理和控制Token的使用。",
      "如果使用自动生成，确保service_secret配置正确，否则Token生成会失败。",
      "Token的签名使用目标服务的service_secret，确保只有目标服务可以验证Token。",
      "定期检查Token的有效期，及时更新过期的Token。",
    ],
  },
  {
    id: "service-token-verification",
    title: "Token验证逻辑",
    category: USAGE_CATEGORIES.SERVICE_TOKEN,
    summary: "详细说明服务Token的验证机制，包括远程验证、本地验证和缓存机制。",
    goal: "帮助开发者理解Token验证的完整流程，正确配置验证模式和缓存策略。",
    steps: [
      {
        title: "验证模式",
        details: [
          "msspace支持三种Token验证模式，通过`SERVICE_TOKEN_VERIFY_MODE`环境变量配置：",
          "",
          "**模式一：远程验证（remote）**：",
          "  - msspace将Token转发给msLogin验证",
          "  - msspace不需要知道自己的service_secret",
          "  - 每次验证都需要网络请求",
          "",
          "**模式二：带缓存的远程验证（cached-remote，默认推荐）**：",
          "  - 远程验证 + 本地缓存",
          "  - 首先检查本地缓存，如果缓存命中且有效，直接使用，不调用msLogin",
          "  - 如果缓存未命中或已过期，调用msLogin验证",
          "  - 验证结果会缓存，减少网络请求",
          "",
          "**模式三：本地验证（local）**：",
          "  - msspace自己验证Token，需要从数据库查询service_secret",
          "  - 需要配置SERVICE_DATABASE_URL（指向msLogin的数据库）",
          "  - 不依赖msLogin服务，验证速度快",
        ],
      },
      {
        title: "默认验证流程（cached-remote）",
        details: [
          "1. **检查本地缓存**：",
          "   - 计算Token的哈希值（SHA256）作为缓存键：`token:verify:{tokenHash}`",
          "   - 如果缓存命中，检查缓存是否过期（基于Token的exp字段）",
          "",
          "2. **版本号检查**：",
          "   - 如果缓存未过期，检查版本号（定期检查，默认30秒间隔）",
          "   - 如果版本号未更新 → 直接使用缓存结果 ✅（不调用msLogin）",
          "   - 如果版本号已更新 → 清除所有Token缓存，继续远程验证",
          "",
          "3. **远程验证**（如果缓存未命中或已过期）：",
          "   - 调用msLogin：`POST /api/auth/verify`",
          "   - msLogin解析Token，获取service字段（如\"msspace\"）",
          "   - msLogin查询数据库：`SELECT service_secret FROM services WHERE service_name = 'msspace'`",
          "   - msLogin使用msspace的service_secret验证Token签名",
          "   - msLogin返回验证结果",
          "",
          "4. **缓存验证结果**：",
          "   - 如果有效，缓存验证结果（包含payload、版本号、过期时间）",
          "   - 设置缓存TTL：`min(Token剩余时间, 配置的TTL)`（默认5分钟）",
          "   - 允许访问 ✅",
        ],
      },
      {
        title: "缓存机制",
        details: [
          "**缓存键**：`token:verify:{tokenHash}`（基于Token的SHA256哈希）",
          "",
          "**缓存内容**：",
          "  - `payload`：Token的载荷信息",
          "  - `version`：Token版本号",
          "  - `expiresAt`：Token过期时间",
          "  - `cachedAt`：缓存时间",
          "",
          "**缓存TTL**：",
          "  - 取Token剩余时间和配置TTL的较小值",
          "  - 默认配置TTL：5分钟",
          "  - 如果Token没有过期时间，使用配置的TTL",
          "",
          "**版本号检查**：",
          "  - 每30秒检查一次（可配置）",
          "  - 如果版本号更新，清除所有Token缓存",
          "  - 确保Token撤销后能及时生效",
        ],
      },
      {
        title: "配置示例",
        details: [
          "**默认配置（推荐）**：",
          "```bash",
          "# msspace 环境变量",
          "SERVICE_TOKEN_VERIFY_MODE=cached-remote",
          "LOGIN_API_URL=http://localhost:50090",
          "SERVICE_TOKEN_CACHE_TTL=300000  # 5分钟（毫秒）",
          "SERVICE_TOKEN_VERSION_CHECK_INTERVAL=30000  # 30秒（毫秒）",
          "```",
          "",
          "**本地验证配置**：",
          "```bash",
          "# msspace 环境变量",
          "SERVICE_TOKEN_VERIFY_MODE=local",
          "SERVICE_DATABASE_URL=\"postgresql://user:pass@host:5432/dbname\"",
          "```",
        ],
      },
    ],
    tips: [
      "默认使用cached-remote模式，兼顾安全性和性能。",
      "缓存机制可以显著减少对msLogin的网络请求，提高验证性能。",
      "版本号检查确保Token撤销后能及时生效，不会因为缓存导致安全问题。",
      "如果msLogin服务不可用，可以考虑使用local模式（需要配置数据库连接）。",
    ],
  },
  {
    id: "service-token-auto-update",
    title: "Token自动更新机制",
    category: USAGE_CATEGORIES.SERVICE_TOKEN,
    summary: "介绍msDevice等服务如何自动获取和更新Token，包括缓存策略和刷新触发条件。",
    goal: "帮助开发者理解Token自动更新机制，正确配置和使用自动更新功能。",
    steps: [
      {
        title: "Token获取优先级",
        details: [
          "1. **环境变量**（如果配置了`MSSPACE_SERVICE_TOKEN`，直接使用，不自动更新）",
          "2. **缓存**（如果Token有效且未过期）",
          "3. **API动态获取**（使用service_secret从msLogin获取）",
        ],
      },
      {
        title: "自动刷新触发条件",
        details: [
          "1. **Token即将过期**：在过期前5分钟自动后台刷新",
          "2. **Token验证失败**：当API调用返回401时，自动清除缓存并重新获取",
          "3. **缓存超时**：如果Token没有过期时间，默认1小时后刷新",
        ],
      },
      {
        title: "工作流程",
        details: [
          "**首次请求**：",
          "  - 检查环境变量 → 没有 → 检查缓存 → 没有 → 调用API获取 → 缓存Token",
          "",
          "**后续请求**：",
          "  - 检查环境变量 → 没有 → 检查缓存 → 有效 → 返回缓存Token",
          "  - 如果Token即将过期 → 后台刷新 → 更新缓存",
          "",
          "**Token失效（401）**：",
          "  - 清除缓存 → 重新获取 → 重试请求",
        ],
      },
      {
        title: "使用示例",
        details: [
          "**msDevice自动获取Token**：",
          "```typescript",
          "// msDevice的SpaceService会自动调用ServiceTokenManager",
          "// 无需手动管理Token",
          "",
          "// 配置service_secret",
          "process.env.MSDEVICE_SERVICE_SECRET = 'your-secret';",
          "",
          "// 调用时会自动获取和更新Token",
          "const spaces = await SpaceService.getSpaces();",
          "```",
          "",
          "**手动获取Token**：",
          "```typescript",
          "import { ServiceTokenManager } from './services/serviceTokenManager';",
          "",
          "// 自动获取Token（会使用配置中的service_secret）",
          "const token = await ServiceTokenManager.getServiceToken(",
          "  'msdevice',  // 源服务名称",
          "  'msspace',   // 目标服务名称",
          ");",
          "",
          "// 手动刷新Token",
          "const newToken = await ServiceTokenManager.refreshToken(",
          "  'msdevice',",
          "  'msspace',",
          "  process.env.MSDEVICE_SERVICE_SECRET",
          ");",
          "```",
        ],
      },
      {
        title: "配置检查清单",
        details: [
          "- [ ] msDevice配置了`MSDEVICE_SERVICE_SECRET`环境变量",
          "- [ ] msAdmin中为msdevice服务配置了service_secret",
          "- [ ] 两个地方的service_secret完全一致",
          "- [ ] msLogin API地址配置正确（`LOGIN_API_URL`或`MSLOGIN_URL`）",
          "- [ ] msDevice可以访问msLogin API",
        ],
      },
    ],
    tips: [
      "只需配置service_secret，系统会自动处理Token的获取和更新。",
      "自动更新机制可以减少手动管理Token的工作量，提高系统可靠性。",
      "如果Token持续获取失败，检查service_secret是否正确配置。",
      "建议使用自动更新机制，而不是手动管理Token。",
    ],
  },
  {
    id: "service-secret-explanation",
    title: "Service Secret使用说明",
    category: USAGE_CATEGORIES.SERVICE_TOKEN,
    summary: "详细说明service_secret的作用和使用方法，包括每个服务应该保存什么密钥。",
    goal: "帮助开发者理解service_secret的正确使用方法，避免配置错误。",
    steps: [
      {
        title: "核心原则",
        details: [
          "**每个服务只保存自己的service_secret**：",
          "  - msDevice保存：`\"this-secert-is-for-device-service\"`",
          "  - msspace保存：`\"service-space-secret-name\"`",
          "",
          "**msDevice不需要知道msspace的密钥**：",
          "  - msDevice只需要自己的密钥来向msLogin证明身份",
          "  - msLogin负责使用msspace的密钥签名Token",
        ],
      },
      {
        title: "完整流程",
        details: [
          "1. **msDevice配置自己的service_secret**：",
          "   ```bash",
          "   MSDEVICE_SERVICE_SECRET=\"this-secert-is-for-device-service\"",
          "   ```",
          "",
          "2. **msDevice请求Token**：",
          "   ```json",
          "   POST /api/service-token/get-token",
          "   {",
          '     "service_name": "msdevice",',
          '     "target_service": "msspace",',
          '     "service_secret": "this-secert-is-for-device-service"  ← msDevice自己的',
          "   }",
          "   ```",
          "",
          "3. **msLogin验证**：",
          "   - 查询数据库：`SELECT service_secret FROM services WHERE service_name = 'msdevice'`",
          "   - 得到：`'this-secert-is-for-device-service'`",
          "   - 比较：请求中的密钥 == 数据库中的密钥？",
          "   - 如果一致 → 验证通过",
          "",
          "4. **msLogin生成Token**：",
          "   - 查询msspace的service_secret：`'service-space-secret-name'`",
          "   - 使用msspace的密钥签名Token",
          "   - 返回Token给msDevice",
          "",
          "5. **msDevice使用Token访问msspace**：",
          "   ```bash",
          "   GET /api/service/spaces/tree",
          "   Authorization: Bearer <token>",
          "   ```",
          "",
          "6. **msspace验证Token**（默认：带缓存的远程验证）：",
          "   - 首先检查本地缓存",
          "   - 如果缓存未命中，将Token转发给msLogin验证",
          "   - msLogin使用msspace的service_secret验证Token签名",
          "   - 验证通过 → msspace返回数据",
        ],
      },
      {
        title: "配置示例",
        details: [
          "**msDevice环境变量**：",
          "```bash",
          "# 正确：使用msDevice自己的service_secret",
          "export MSDEVICE_SERVICE_SECRET=\"this-secert-is-for-device-service\"",
          "",
          "# 错误：不要使用msspace的密钥",
          "# export MSDEVICE_SERVICE_SECRET=\"service-space-secret-name\"  ❌",
          "```",
          "",
          "**msAdmin中配置**：",
          "1. 编辑`msdevice`服务",
          "2. 设置`service_secret` = `\"this-secert-is-for-device-service\"`",
          "3. 保存",
          "",
          "**重要**：msDevice的环境变量必须与数据库中msdevice服务的service_secret完全一致！",
        ],
      },
      {
        title: "常见错误",
        details: [
          "**错误1：使用目标服务的密钥**：",
          "  - ❌ msDevice使用msspace的service_secret",
          "  - ✅ msDevice应该使用自己的service_secret",
          "",
          "**错误2：密钥不一致**：",
          "  - ❌ 环境变量中的密钥与数据库中的密钥不一致",
          "  - ✅ 确保两个地方的密钥完全一致",
          "",
          "**错误3：变量名混淆**：",
          "  - ❌ `SERVICE_SPACE_SECRET_NAME`看起来像是msspace的密钥",
          "  - ✅ 应该存储msDevice自己的密钥，建议使用`MSDEVICE_SERVICE_SECRET`",
        ],
      },
    ],
    tips: [
      "每个服务只需要知道自己的service_secret，不需要知道其他服务的密钥。",
      "service_secret必须与数据库中对应服务的service_secret完全一致。",
      "建议统一使用`MSDEVICE_SERVICE_SECRET`这样的命名，避免混淆。",
      "定期检查service_secret的一致性，确保配置正确。",
    ],
  },
];

export const getUsageCategories = (): string[] => {
  return Object.values(USAGE_CATEGORIES);
};

export const getSectionsByCategory = (category: string): UsageSection[] => {
  return USAGE_SECTIONS.filter((section) => section.category === category);
};

export const getSectionById = (id: string): UsageSection | undefined => {
  return USAGE_SECTIONS.find((section) => section.id === id);
};
